import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail, formatINR } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Loader2, Upload, Save, Building2, Crown, Palette, FileText, DoorOpen, DoorClosed, Sun, Siren, Globe, Copy, ExternalLink, Smartphone, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const THEME_PRESETS = [
  { key: "classic", label: "Classic", desc: "Solid header band with clinic name. Safe, professional." },
  { key: "modern", label: "Modern", desc: "Minimal left accent strip. Best for clean, design-led clinics." },
  { key: "bold", label: "Bold", desc: "Large coloured banner. Eye-catching for branded handouts." },
];

const STATUS_OPTIONS = [
  { key: "open", label: "Open", icon: DoorOpen, color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  { key: "closed", label: "Closed", icon: DoorClosed, color: "text-slate-600 bg-slate-50 border-slate-200" },
  { key: "holiday", label: "Holiday", icon: Sun, color: "text-amber-600 bg-amber-50 border-amber-200" },
  { key: "emergency", label: "Emergency closure", icon: Siren, color: "text-red-600 bg-red-50 border-red-200" },
];

export default function Settings() {
  const [form, setForm] = useState({
    name: "",
    address: "",
    phone: "",
    gstin: "",
    license_number: "",
    default_consultation_fee: "",
    primary_color: "#2563EB",
    tagline: "",
    pdf_theme: "classic",
    upi_vpa: "",
    upi_payee_name: "",
  });
  const [logoFileId, setLogoFileId] = useState(null);
  const [bannerFileId, setBannerFileId] = useState(null);
  const [statusForm, setStatusForm] = useState({
    status: "open",
    status_message: "",
    status_until: "",
    notify_patients: false,
  });
  const [slug, setSlug] = useState("");
  const [slugDraft, setSlugDraft] = useState("");
  const [savingSlug, setSavingSlug] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [plan, setPlan] = useState(null);
  const [planExpiresAt, setPlanExpiresAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);
  const bannerRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clinic/settings");
      setForm({
        name: data.name || "",
        address: data.address || "",
        phone: data.phone || "",
        gstin: data.gstin || "",
        license_number: data.license_number || "",
        default_consultation_fee: data.default_consultation_fee != null ? String(data.default_consultation_fee) : "",
        primary_color: data.primary_color || "#2563EB",
        tagline: data.tagline || "",
        pdf_theme: data.pdf_theme || "classic",
        upi_vpa: data.upi_vpa || "",
        upi_payee_name: data.upi_payee_name || "",
      });
      setLogoFileId(data.logo_file_id || null);
      setBannerFileId(data.banner_file_id || null);
      setSlug(data.slug || "");
      setSlugDraft(data.slug || "");
      setStatusForm({
        status: data.status || "open",
        status_message: data.status_message || "",
        status_until: data.status_until ? String(data.status_until).slice(0,10) : "",
        notify_patients: false,
      });
      setPlan(data.current_plan || data.plan);
      setPlanExpiresAt(data.plan_expires_at);
    } catch (e) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { ...form };
      if (payload.default_consultation_fee === "") {
        payload.default_consultation_fee = null;
      } else {
        payload.default_consultation_fee = parseFloat(payload.default_consultation_fee);
      }
      await api.put("/clinic/settings", payload);
      toast.success("Settings saved");
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/clinic/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogoFileId(data.file_id);
      toast.success("Logo uploaded");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Upload failed");
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/clinic/banner", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBannerFileId(data.file_id);
      toast.success("Banner uploaded");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Upload failed");
    }
  };

  const handleStatusSave = async () => {
    setSavingStatus(true);
    try {
      const { data } = await api.put("/clinic/status", {
        status: statusForm.status,
        status_message: statusForm.status_message,
        status_until: statusForm.status_until || null,
        notify_patients: statusForm.notify_patients,
      });
      if (data.warning) toast.warning(data.warning);
      else if (statusForm.notify_patients && statusForm.status !== "open") {
        toast.success(`Status updated. Notified ${data.notified || 0} patient(s).`);
      } else {
        toast.success("Clinic status updated");
      }
      setStatusForm((s) => ({ ...s, notify_patients: false }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSavingStatus(false);
    }
  };

  const handleSlugSave = async () => {
    setSavingSlug(true);
    try {
      const { data } = await api.put("/clinic/slug", { slug: slugDraft });
      setSlug(data.slug);
      setSlugDraft(data.slug);
      toast.success("Microsite URL updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSavingSlug(false);
    }
  };

  const copyMicrositeUrl = () => {
    const url = `${window.location.origin}/c/${slug}`;
    navigator.clipboard?.writeText(url);
    toast.success("Microsite link copied");
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const planColors = {
    trial: "bg-slate-100 text-slate-700 border-slate-200",
    basic: "bg-blue-50 text-blue-700 border-blue-200",
    pro: "bg-emerald-50 text-emerald-700 border-emerald-200",
    expired: "bg-red-50 text-red-700 border-red-200",
    cancelled: "bg-red-50 text-red-700 border-red-200",
  };

  return (
    <div className="p-6 lg:p-10 max-w-4xl mx-auto fade-in" data-testid="settings-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-600 mb-2">
          Settings
        </div>
        <h1 className="text-3xl lg:text-4xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>
          Clinic settings
        </h1>
        <p className="text-slate-600 mt-1">Update your clinic profile, branding, and defaults.</p>
      </div>

      {/* Plan banner */}
      <Card className="border-slate-200 mb-6" data-testid="settings-plan-card">
        <CardContent className="p-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Crown className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-sm font-medium text-slate-900">
                Current plan: <Badge variant="outline" className={`ml-2 ${planColors[plan] || planColors.trial}`}>{plan?.toUpperCase() || "TRIAL"}</Badge>
              </div>
              {planExpiresAt && (
                <div className="text-xs text-slate-500 mt-0.5">
                  Renews on {new Date(planExpiresAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </div>
              )}
            </div>
          </div>
          <Link to="/billing/upgrade">
            <Button variant="outline" size="sm" data-testid="settings-manage-plan-btn">Manage subscription</Button>
          </Link>
        </CardContent>
      </Card>

      {/* Document Templates shortcut */}
      <Card className="border-slate-200 mb-6" data-testid="templates-shortcut-card">
        <Link to="/settings/templates" className="block group">
          <CardContent className="p-5 flex items-center gap-4 hover:bg-slate-50/60 transition-colors">
            <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center">
              <FileText className="w-5 h-5 text-violet-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900">Document templates</div>
              <div className="text-xs text-slate-500 mt-0.5">
                Upload your own .docx prescriptions, invoices and certificates. We merge in real data when you print.
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-slate-700 transition-colors" />
          </CardContent>
        </Link>
      </Card>

      {/* Public Microsite URL card */}
      <Card className="border-slate-200 mb-6" data-testid="microsite-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <Globe className="w-5 h-5 text-slate-500" /> Public microsite
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Your patients can find you, learn about your clinic, and book appointments — all from a custom URL you own. Lowercase letters, numbers, and hyphens only.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <Label>Microsite identifier (slug)</Label>
            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1 px-3 py-2 rounded-md bg-slate-100 border border-slate-200 text-sm text-slate-500 font-mono">
                <Input
                  value={slugDraft}
                  onChange={(e) => setSlugDraft(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                  maxLength={48}
                  placeholder="your-clinic"
                  className="h-7 px-1 bg-transparent border-0 focus-visible:ring-0 font-mono text-sm text-slate-900 w-40"
                  data-testid="settings-slug-input"
                />
                <span>.hedesk.help</span>
              </div>
              {slugDraft !== slug && (
                <Button onClick={handleSlugSave} size="sm" disabled={savingSlug || slugDraft.length < 3} data-testid="save-slug-btn">
                  {savingSlug ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Save
                </Button>
              )}
            </div>
            {slugDraft.length > 0 && slugDraft.length < 3 && (
              <p className="text-xs text-amber-600 mt-1.5">Slug must be at least 3 characters.</p>
            )}
          </div>

          {slug && slugDraft === slug && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-blue-50/40 border border-blue-100">
                <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold mb-1">Subdomain (recommended)</div>
                <div className="text-sm font-mono text-slate-900 break-all">{slug}.hedesk.help</div>
                <div className="mt-2 flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(`https://${slug}.hedesk.help`); toast.success("Subdomain copied"); }} data-testid="copy-subdomain-btn">
                    <Copy className="w-3 h-3 mr-1" /> Copy
                  </Button>
                  <a
                    href={`https://${slug}.hedesk.help`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center text-xs text-blue-600 hover:underline gap-1"
                    data-testid="open-subdomain-link"
                  >
                    Open <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Path-based fallback</div>
                <div className="text-sm font-mono text-slate-900 break-all">hedesk.help/c/{slug}</div>
                <div className="mt-2 flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={copyMicrositeUrl} data-testid="copy-microsite-btn">
                    <Copy className="w-3 h-3 mr-1" /> Copy
                  </Button>
                  <a
                    href={`/c/${slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center text-xs text-blue-600 hover:underline gap-1"
                    data-testid="open-microsite-link"
                  >
                    Open <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Clinic status card (separate from main form — has own save) */}
      <Card className="border-slate-200 mb-6" data-testid="status-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <DoorOpen className="w-5 h-5 text-slate-500" /> Clinic status
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {STATUS_OPTIONS.map((opt) => {
              const active = statusForm.status === opt.key;
              const Icon = opt.icon;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setStatusForm((s) => ({ ...s, status: opt.key }))}
                  className={`rounded-xl border-2 p-3 flex flex-col items-center gap-2 transition-all ${active ? "border-blue-600 bg-blue-50/40" : "border-slate-200 hover:border-slate-300"}`}
                  data-testid={`status-opt-${opt.key}`}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center border ${opt.color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium text-slate-800">{opt.label}</span>
                </button>
              );
            })}
          </div>

          {statusForm.status !== "open" && (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Status until (optional)</Label>
                  <Input
                    type="date"
                    value={statusForm.status_until}
                    onChange={(e) => setStatusForm((s) => ({ ...s, status_until: e.target.value }))}
                    className="mt-1.5 h-11"
                    data-testid="status-until-input"
                  />
                  <p className="text-xs text-slate-400 mt-1.5">Used to identify affected appointments. Leave blank for next 7 days.</p>
                </div>
                <div>
                  <Label>Message to patients</Label>
                  <Input
                    value={statusForm.status_message}
                    onChange={(e) => setStatusForm((s) => ({ ...s, status_message: e.target.value }))}
                    maxLength={120}
                    placeholder="e.g. Closed for Diwali — reopens Nov 5"
                    className="mt-1.5 h-11"
                    data-testid="status-message-input"
                  />
                </div>
              </div>
              <div className="p-3 rounded-lg border border-blue-100 bg-blue-50/40 text-xs text-blue-800">
                <strong className="font-semibold">Tip:</strong> after saving, use the WhatsApp Quick Action on each affected appointment to notify patients individually — free and instant.
              </div>
            </>
          )}
          <div className="flex justify-end">
            <Button type="button" onClick={handleStatusSave} disabled={savingStatus} className="bg-blue-600 hover:bg-blue-700" data-testid="status-save-btn">
              {savingStatus ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save status
            </Button>
          </div>
        </CardContent>
      </Card>

      <form onSubmit={handleSave}>
        <Card className="border-slate-200 mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
              <Building2 className="w-5 h-5 text-slate-500" /> Clinic profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Clinic name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="settings-name-input" className="mt-1.5 h-11" />
              </div>
              <div className="sm:col-span-2">
                <Label>Address</Label>
                <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} placeholder="Street, locality, city, PIN" data-testid="settings-address-input" className="mt-1.5" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 98765 43210" data-testid="settings-phone-input" className="mt-1.5 h-11" />
              </div>
              <div>
                <Label>GSTIN (optional)</Label>
                <Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} placeholder="22AAAAA0000A1Z5" maxLength={15} data-testid="settings-gstin-input" className="mt-1.5 h-11" />
              </div>
              <div>
                <Label>License / Registration number</Label>
                <Input value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} placeholder="MCI/MMC reg no." data-testid="settings-license-input" className="mt-1.5 h-11" />
              </div>
              <div>
                <Label>Default consultation fee (₹)</Label>
                <Input type="number" min="0" step="50" value={form.default_consultation_fee} onChange={(e) => setForm({ ...form, default_consultation_fee: e.target.value })} placeholder="500" data-testid="settings-fee-input" className="mt-1.5 h-11" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* UPI payments card */}
        <Card className="border-slate-200 mb-6" data-testid="upi-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
              <Smartphone className="w-5 h-5 text-violet-500" /> UPI payments
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Add your clinic&apos;s UPI ID to show a scannable QR at the billing desk. Patients pay directly from any UPI app (GPay/PhonePe/Paytm) into your bank — no platform fees.
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>UPI ID (VPA)</Label>
                <Input
                  value={form.upi_vpa}
                  onChange={(e) => setForm({ ...form, upi_vpa: e.target.value.trim().toLowerCase() })}
                  placeholder="clinic@upi"
                  data-testid="settings-upi-vpa-input"
                  className="mt-1.5 h-11 font-mono"
                  maxLength={64}
                />
                <p className="text-xs text-slate-400 mt-1.5">Format: <span className="font-mono">name@bank</span> · e.g. <span className="font-mono">drsharma@hdfcbank</span></p>
              </div>
              <div>
                <Label>Payee name (shown in UPI app)</Label>
                <Input
                  value={form.upi_payee_name}
                  onChange={(e) => setForm({ ...form, upi_payee_name: e.target.value })}
                  placeholder={form.name || "Clinic name"}
                  data-testid="settings-upi-payee-input"
                  className="mt-1.5 h-11"
                  maxLength={50}
                />
                <p className="text-xs text-slate-400 mt-1.5">Leave blank to use your clinic name.</p>
              </div>
            </div>
            {form.upi_vpa && form.upi_vpa.includes("@") && (
              <div className="mt-4 p-3 rounded-lg bg-violet-50/50 border border-violet-100 text-xs text-violet-800">
                <strong className="font-semibold">Ready:</strong> A "Show UPI QR" button will appear on the Quick Bill dialog and unpaid invoices in Billing.
              </div>
            )}
            {form.upi_vpa && !form.upi_vpa.includes("@") && (
              <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                UPI ID must contain <span className="font-mono">@</span> — e.g. <span className="font-mono">drsharma@hdfcbank</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200 mb-6">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: "Manrope" }}>Logo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6 flex-wrap">
              <div className="w-24 h-24 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                {logoFileId ? (
                  <img
                    src={`${BACKEND_URL}/api/public/files/${logoFileId}`}
                    alt="Clinic logo"
                    className="w-full h-full object-contain"
                    data-testid="settings-logo-preview"
                  />
                ) : (
                  <span className="text-xs text-slate-400">No logo</span>
                )}
              </div>
              <div>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={handleLogoUpload} className="hidden" data-testid="settings-logo-input" />
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="settings-logo-upload-btn">
                  <Upload className="w-4 h-4 mr-2" /> {logoFileId ? "Replace logo" : "Upload logo"}
                </Button>
                <p className="text-xs text-slate-400 mt-2">PNG, JPG, WebP, or SVG. Max 5MB. Appears on invoices and shared links.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 mb-6" data-testid="branding-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
              <Palette className="w-5 h-5 text-slate-500" /> Branding
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Banner */}
            <div>
              <Label>Banner image (for microsite hero)</Label>
              <div className="mt-1.5 flex items-center gap-4 flex-wrap">
                <div className="w-48 h-24 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                  {bannerFileId ? (
                    <img src={`${BACKEND_URL}/api/public/files/${bannerFileId}`} alt="Banner" className="w-full h-full object-cover" data-testid="settings-banner-preview" />
                  ) : (
                    <span className="text-xs text-slate-400">No banner</span>
                  )}
                </div>
                <div>
                  <input ref={bannerRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleBannerUpload} className="hidden" data-testid="settings-banner-input" />
                  <Button type="button" variant="outline" onClick={() => bannerRef.current?.click()} data-testid="settings-banner-upload-btn">
                    <Upload className="w-4 h-4 mr-2" /> {bannerFileId ? "Replace banner" : "Upload banner"}
                  </Button>
                  <p className="text-xs text-slate-400 mt-2">Wide image. Max 10MB. Used on public microsite (coming).</p>
                </div>
              </div>
            </div>

            {/* Color + Tagline */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Primary color</Label>
                <div className="mt-1.5 flex items-center gap-3">
                  <input
                    type="color"
                    value={form.primary_color}
                    onChange={(e) => setForm({ ...form, primary_color: e.target.value.toUpperCase() })}
                    className="h-11 w-14 rounded-md cursor-pointer border border-slate-200"
                    data-testid="settings-color-input"
                  />
                  <Input
                    value={form.primary_color}
                    onChange={(e) => setForm({ ...form, primary_color: e.target.value.toUpperCase() })}
                    maxLength={7}
                    className="h-11 font-mono uppercase"
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1.5">Applied to PDFs, microsite, and key UI accents.</p>
              </div>
              <div>
                <Label>Tagline (optional)</Label>
                <Input
                  value={form.tagline}
                  onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                  placeholder="Quality care, close to home."
                  maxLength={60}
                  className="mt-1.5 h-11"
                  data-testid="settings-tagline-input"
                />
                <p className="text-xs text-slate-400 mt-1.5">Shown on PDFs and microsite header.</p>
              </div>
            </div>

            {/* PDF Theme presets */}
            <div>
              <Label className="flex items-center gap-2 mb-2"><FileText className="w-4 h-4 text-slate-500" /> PDF template</Label>
              <RadioGroup value={form.pdf_theme} onValueChange={(v) => setForm({ ...form, pdf_theme: v })} className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2" data-testid="theme-radio-group">
                {THEME_PRESETS.map((t) => (
                  <label
                    key={t.key}
                    htmlFor={`theme-${t.key}`}
                    className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all ${form.pdf_theme === t.key ? "border-blue-600 bg-blue-50/40" : "border-slate-200 hover:border-slate-300"}`}
                    data-testid={`theme-${t.key}`}
                  >
                    <RadioGroupItem value={t.key} id={`theme-${t.key}`} className="sr-only" />
                    {/* Mini preview */}
                    <div className="w-full h-14 rounded-md mb-3 overflow-hidden border border-slate-100 relative">
                      {t.key === "classic" && (
                        <>
                          <div className="absolute top-0 left-0 right-0 h-6" style={{ backgroundColor: form.primary_color }} />
                          <div className="absolute top-7 left-2 right-2 space-y-1">
                            <div className="h-1 bg-slate-200 rounded w-2/3" />
                            <div className="h-1 bg-slate-100 rounded w-1/3" />
                          </div>
                        </>
                      )}
                      {t.key === "modern" && (
                        <>
                          <div className="absolute top-0 bottom-0 left-0 w-1.5" style={{ backgroundColor: form.primary_color }} />
                          <div className="absolute top-2 left-4 right-2 space-y-1">
                            <div className="h-1.5 rounded w-1/2" style={{ backgroundColor: form.primary_color, opacity: .85 }} />
                            <div className="h-1 bg-slate-200 rounded w-2/3" />
                            <div className="h-1 bg-slate-100 rounded w-1/3" />
                          </div>
                        </>
                      )}
                      {t.key === "bold" && (
                        <>
                          <div className="absolute top-0 left-0 right-0 h-10" style={{ backgroundColor: form.primary_color }} />
                          <div className="absolute top-2 left-2 h-1.5 w-1/2 bg-white/80 rounded" />
                          <div className="absolute top-11 left-2 right-2 h-1 bg-slate-200 rounded w-1/2" />
                        </>
                      )}
                    </div>
                    <div className="font-semibold text-sm text-slate-900">{t.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5 leading-snug">{t.desc}</div>
                  </label>
                ))}
              </RadioGroup>
            </div>
          </CardContent>
        </Card>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}

        <div className="flex justify-end">
          <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="settings-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save changes
          </Button>
        </div>
      </form>
    </div>
  );
}
